import { Form, Formik } from 'formik';
import { useEffect } from 'react';
import * as bd from 'react-basic-design';
import { LicenseCodeDTO, useGenerateCodeApi } from '../../../api/license-code-api';
import { ProductDTO } from '../../../api/product-api';
import SvgClose from '../../../assets/icons/Close';
import SvgDelete from '../../../assets/icons/Delete';
import SvgPhotoPrint from '../../../assets/icons/PhotoPrint';
import SvgRefresh from '../../../assets/icons/Refresh';
import SvgSave from '../../../assets/icons/Save';
import { BasicModal } from '../../../components/basic/basic-modal';
import { Loading } from '../../../components/basic/loading';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { yup } from '../../../components/basic/yup';
import { FormikCellToggle, FormikTextField } from '../../../components/formik';
import SvgPrint from '../../../assets/icons/Print';

interface SaveLicenseCodeModalProps {
    show: boolean;
    allowEdit: boolean;
    selectedLicenseCode?: LicenseCodeDTO;
    products: ProductDTO[];
    onHide: () => void;
    onDownloadCode: (licenseCode: LicenseCodeDTO) => void;
    onSave: (values: LicenseCodeDTO) => void;
    onDelete?: (licenseCode: LicenseCodeDTO) => void;
}

export function SaveLicenseCodeModal({
    show,
    allowEdit,
    selectedLicenseCode,
    products,
    onDownloadCode,
    onHide,
    onSave,
    onDelete,
}: SaveLicenseCodeModalProps) {
    const generateCodeApi = useGenerateCodeApi();
    const insertMode = !selectedLicenseCode;
    const { initialized, initializing, error, result } = generateCodeApi;
    const LicenseCodeSchema = yup.object().shape({
        code: yup.string().required(' '),
        productId: yup.number().required(' '),
        downloaded: yup.boolean().required(' '),
    });

    const defaultValues: LicenseCodeDTO = {
        serial: selectedLicenseCode ? selectedLicenseCode.serial : 0,
        code: selectedLicenseCode ? selectedLicenseCode.code : '',
        productId: selectedLicenseCode ? selectedLicenseCode.productId : 0,
        downloaded: selectedLicenseCode ? selectedLicenseCode.downloaded : false,
    };

    useEffect(() => {
        if (!show) return;
        generateCodeApi.get();
    }, [show]);

    if (!show) return <></>;

    if (insertMode && (!initialized || initializing))
        return <Loading modal show={show} onHide={onHide} />;

    return (
        <Formik
            initialValues={{
                ...defaultValues,
                code: insertMode ? result.code : defaultValues.code,
            }}
            validationSchema={LicenseCodeSchema}
            onSubmit={onSave}
        >
            {formik => (
                <BasicModal
                    show={show}
                    onHide={onHide}
                    size="lg"
                    title={
                        <bd.Flex className="w-100" align="center" content="between">
                            <bd.Flex align="center">
                                <T>
                                    {selectedLicenseCode ? 'edit-license-code' : 'new-license-code'}
                                </T>
                                {!insertMode && (
                                    <FormikCellToggle
                                        label={
                                            selectedLicenseCode.downloaded ? (
                                                <T>downloaded</T>
                                            ) : (
                                                <T>not-downloaded</T>
                                            )
                                        }
                                        className="small"
                                        name="downloaded"
                                        disabled={selectedLicenseCode.downloaded}
                                        readOnly={!allowEdit}
                                    />
                                )}
                            </bd.Flex>
                            <bd.Flex align="center" gap={2}>
                                {!insertMode && (
                                    <bd.Button
                                        type="button"
                                        variant="icon"
                                        size="sm"
                                        title={translate('download')}
                                        disabled={selectedLicenseCode.downloaded}
                                        onClick={() => onDownloadCode(selectedLicenseCode)}
                                    >
                                        <SvgPrint />
                                    </bd.Button>
                                )}
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={formik.submitForm}
                                    disabled={!insertMode && selectedLicenseCode.downloaded}
                                >
                                    <SvgSave />
                                </bd.Button>
                                {selectedLicenseCode && onDelete && (
                                    <bd.Button
                                        variant="icon"
                                        color="secondary"
                                        size="sm"
                                        onClick={() => onDelete(selectedLicenseCode)}
                                        disabled={!insertMode && selectedLicenseCode.downloaded}
                                    >
                                        <SvgDelete />
                                    </bd.Button>
                                )}
                                <bd.Button
                                    variant="icon"
                                    color="secondary"
                                    size="sm"
                                    onClick={onHide}
                                >
                                    <SvgClose />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    }
                >
                    <Form>
                        <bd.Flex vertical>
                            <FormikTextField
                                label="code"
                                name="code"
                                readOnly={!allowEdit}
                                width="20rem"
                                disabled={!insertMode && selectedLicenseCode.downloaded}
                                buttonIcon={insertMode ? <SvgRefresh /> : undefined}
                                buttonOnClick={() =>
                                    insertMode &&
                                    generateCodeApi
                                        .get()
                                        .then(r => formik.setFieldValue('code', r.code))
                                        .catch(notify.error)
                                }
                                handleEnter
                            />

                            <FormikTextField
                                type="select"
                                label="product-id"
                                name="productId"
                                disabled={!insertMode && selectedLicenseCode.downloaded}
                                readOnly={!allowEdit}
                                width="20rem"
                                items={[
                                    { id: null, title: '' },
                                    ...products.map(p => ({ id: p.id, title: p.name })),
                                ]}
                            />
                        </bd.Flex>
                    </Form>
                </BasicModal>
            )}
        </Formik>
    );
}
